/* eslint-disable unicorn/consistent-function-scoping */
import Axios, { AxiosResponse, CancelTokenSource } from 'axios';
import FileSaver from 'file-saver';
import moment from 'moment';
import {
  GetTransactionsCount,
  GetExportTransactions,
  GetTypeConfigurationRequest,
  GetExportMultiAssetSearchReport,
  GetTransactions,
} from '../../services/types';
import { transactionsActionNames, Details } from './types';
import Transactions from '../../services/transactions';
import { AppState, AppThunkDispatch } from '..';
import { enqueueSnackbarAction } from '../snackbar/actions';
import FileTypeConfiguration from '../../services/file-type-configurations';
import { FileTypeConfigResponse } from '../../entities/file-type-configurations-v1/models';
import { downloadFromUrl } from '../../globals/utils';
import Assets from '../../services/assets-v2';
import { DeviceWithAssetAndRadioData } from '../../entities/device-radios-v2/device-with-asset-and-radio-data-response';
import { AssetsResponse } from '../../entities/assets-v2';
import { TransactionsResponse } from '../../entities/transactions-v1/transaction-response';
let ajaxRequest: CancelTokenSource;

export const getTotalCountOfTransactions =
  (input: GetTransactionsCount) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      transactions: { ajaxRequestCancelTokenForCount },
    } = getState();
    const { timeFrame } = input;
    const transactions = new Transactions(client, envVariables.transactionsV1BaseUrl);
    const response = await transactions.getCount(input, ajaxRequestCancelTokenForCount?.token);
    if (response.data > 10000) {
      dispatch(
        enqueueSnackbarAction({
          message:
            'Transactions limit too high, please refine transactions using the filters to select fewer than 10,000 transactions',
          options: { variant: 'error' },
        }),
      );
    }
    dispatch({ type: transactionsActionNames.SET_TOTAL_COUNT, payload: response.data });
  };

export const updateDetailsMap = (input: Details[]) => async (dispatch: AppThunkDispatch) =>
  dispatch({ type: transactionsActionNames.SET_DETAILS, payload: input });

export const clearDetailsMap = () => async (dispatch: AppThunkDispatch) =>
  dispatch({ type: transactionsActionNames.CLEAR_DETAILS });

export const setRenderringSubComponent = (flag: boolean) => async (dispatch: AppThunkDispatch) => {
  dispatch({
    type: transactionsActionNames.SET_SUBCOMPONENT_RENDERRING_FLAG,
    payload: flag,
  });
};

export const exportTransactionsActions =
  (input: GetExportTransactions) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      try {
        const transactions = new Transactions(
          client,
          envVariables.transactionsV1BaseUrl,
          envVariables.storageV1BaseUrl,
        );
        dispatch({ type: transactionsActionNames.SET_EXPORTABLE, payload: false });
        dispatch(
          enqueueSnackbarAction({
            message:
              'CSV export is generating. In some instances with large exports this can take over 5 minutes. Reducing the date range can reduce this wait time.',
            options: { variant: 'info' },
          }),
        );
        const response = await transactions.exportTransactions(input);
        dispatch({ type: transactionsActionNames.SET_EXPORTABLE, payload: true });
        if (!response) {
          dispatch(
            enqueueSnackbarAction({
              message: 'CSV export failed',
              options: { variant: 'error' },
            }),
          );
        } else {
          downloadFromUrl(response);
          dispatch(
            enqueueSnackbarAction({
              message: 'CSV exported successfully',
              options: { variant: 'success' },
            }),
          );
        }
      } catch (error) {
        dispatch(
          enqueueSnackbarAction({
            message: 'CSV export failed',
            options: { variant: 'error' },
          }),
        );
      }
    };

export const exportMultiAssetSearchReportsActions =
  (input: GetExportMultiAssetSearchReport) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const { login: { client, envVariables } } = getState();
      const promisesAssetSerialNumbers: Promise<AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>>[] = [];
      const promisesTransactions: Promise<AxiosResponse<TransactionsResponse>>[] = [];
      const assets = new Assets(client, envVariables.assetsV2BaseUrl);
      try {
        const transactionsService = new Transactions(
          client,
          envVariables.transactionsV1BaseUrl,
          envVariables.storageV1BaseUrl,
        );
        dispatch(
          enqueueSnackbarAction({
            message:
              'CSV export is generating. In some instances with large exports this can take over 2 minutes.',
            options: { variant: 'info' },
          }),
        );
        if (ajaxRequest) {
          ajaxRequest.cancel();
        }

        ajaxRequest = Axios.CancelToken.source();

        input.assetSerialNumbers.forEach(assetSerialNumber => {
          if (assetSerialNumber.trim() !== '') {
            promisesAssetSerialNumbers.push(assets.getByAssetSerialNumber(assetSerialNumber.trim()));
          }
        });

        const successfulEntityReturn: DeviceWithAssetAndRadioData[] = [];
        const responsesAssets = await Promise.allSettled(promisesAssetSerialNumbers);

        responsesAssets
          .filter(
            (
              response: PromiseSettledResult<
                AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>
              >,
            ) => response.status === 'fulfilled',
          )
          .forEach((responseSuccessful: any) => {
            responseSuccessful.value.data?.forEach((objectDevice: any) => {
              successfulEntityReturn.push(objectDevice);
            });
          });

        if (successfulEntityReturn.length > 0) {
          const deviceKeys: any[] = [];
          const telemeticsAssetIds: any[] = [];
          successfulEntityReturn.forEach((successfulEntities: any) => {
            if (successfulEntities?.devices.length > 0) {
              // With device
              successfulEntities.devices.forEach((device: any, indexDevice: number) => {
                deviceKeys.push(device.deviceKey);
              });
              successfulEntities.devices.forEach((device: any, indexDevice: number) => {
                device?.radios.forEach((radios: any, indexDevice: number) => {
                  deviceKeys.push(radios?.deviceKey);
                });
              });
              if (input?.vimsData === true) {
                telemeticsAssetIds.push(successfulEntities.telematicsAssetId);
              }
            } else {
              // Without device
              telemeticsAssetIds.push(successfulEntities.telematicsAssetId);
            }
          });

          if (input?.vimsData === true) {
            telemeticsAssetIds.forEach((value: any) => {
              const transactionsRequest: GetTransactions = {
                startTime: input.startTime,
                endTime: input.endTime,
                telematicsAssetId: value,
                limit: 200,
              };
              promisesTransactions.push(transactionsService.getAll(transactionsRequest, ajaxRequest.token));
            });
          }
          else {
            telemeticsAssetIds.forEach((value: any) => {
              const transactionsRequest: GetTransactions = {
                startTime: input.startTime,
                endTime: input.endTime,
                telematicsAssetId: value,
                limit: 200,
              };
              promisesTransactions.push(transactionsService.getAll(transactionsRequest, ajaxRequest.token));
            });
            deviceKeys.forEach((value: any) => {
              const transactionsRequest: GetTransactions = {
                startTime: input.startTime,
                endTime: input.endTime,
                deviceKey: value,
                limit: 200,
              };
              promisesTransactions.push(transactionsService.getAll(transactionsRequest, ajaxRequest.token));
            });
          }

          let transactions: any[] = [];
          const responsesTransactions = await Promise.allSettled(promisesTransactions);
          responsesTransactions
            .filter(
              (
                response: PromiseSettledResult<AxiosResponse<any>>,
              ) => response.status === 'fulfilled',
            )
            .forEach((responseSuccessful: any) => {
              const response = responseSuccessful.value.data;
              if (response) {
                const { transaction } = response.transactions;
                if (transaction) {
                  transactions = [...transactions, ...transaction];
                }
              }
            });
          if (transactions.length > 0) {
            try {
              /* eslint-disable @typescript-eslint/no-var-requires */
              /* eslint import/newline-after-import: "off" */
              /* eslint-disable global-require */
              const { Parser } = require('@json2csv/plainjs');
              const parser = new Parser();
              const csv = await parser.parse(transactions);
              const blob = new Blob(
                [csv],
                {
                  type: 'text/plain;charset=utf-8',
                },
              );
              await FileSaver.saveAs(blob, `Transactions_Report_${moment().format('MMMM_Do_YYYY_h:mm:ss_a')}.csv`);
              dispatch(
                enqueueSnackbarAction({
                  message: 'CSV exported successfully',
                  options: { variant: 'success' },
                }),
              );
            } catch (error) {
              console.error(error);
            }
          } else {
            dispatch(
              enqueueSnackbarAction({
                message: 'CSV export failed',
                options: { variant: 'error' },
              }),
            );
          }
        } else {
          dispatch(
            enqueueSnackbarAction({
              message: 'CSV export failed',
              options: { variant: 'error' },
            }),
          );
        }
      } catch (error) {
        dispatch(
          enqueueSnackbarAction({
            message: 'CSV export failed',
            options: { variant: 'error' },
          }),
        );
      }
    };

export enum transferDirection {
  MobileTerminated = 'mobileTerminated',
  Both = 'both',
  MobileOriginated = 'mobileOriginated',
}

export enum fileType {
  DataFile = 'dataFile',
  PLMessage = 'PLMessage',
  Config = 'config',
}
export enum Suffix {
  Command = 'Command',
  Message = 'Message',
  File = 'File',
  Config = 'Config',
  FileCommand = 'File/Command',
  MessageCommand = 'Message/Command',
  ConfigCommand = 'Config/Command',
}

const formalizeFileTypes = (classification: string, direction: string) => {
  if (direction === transferDirection.MobileTerminated && classification) return Suffix.Command;
  if (direction === transferDirection.MobileOriginated) {
    if (classification === fileType.PLMessage) return Suffix.Message;
    if (classification === fileType.DataFile) return Suffix.File;
    if (classification === fileType.Config) return Suffix.Config;
  }
  if (direction === transferDirection.Both) {
    if (classification === fileType.DataFile) return Suffix.FileCommand;
    if (classification === fileType.PLMessage) return Suffix.MessageCommand;
    if (classification === fileType.Config) return Suffix.ConfigCommand;
  }
  return null;
};

export const getAllFileTypes =
  (input: GetTypeConfigurationRequest) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const fileTypeList = new FileTypeConfiguration(client, envVariables.fileTypeConfigV2BaseUrl);
      const response: AxiosResponse<FileTypeConfigResponse> =
        await fileTypeList.getFileTypeConfigurationRecords(input);
      if (response.data.fileTypeConfigs) {
        const formattedResponse = response.data.fileTypeConfigs
          .sort((a, b) => {
            const fileA = a.fileTypeName?.toLowerCase() || '';
            const fileB = b.fileTypeName?.toLowerCase() || '';
            if (fileA < fileB) {
              return -1;
            }
            if (fileA > fileB) {
              return 1;
            }
            return 0;
          })
          .map((fileTypeConfig) => {
            const { classification, transferDirection } = fileTypeConfig;
            const type =
              classification &&
              transferDirection &&
              formalizeFileTypes(classification, transferDirection);
            return {
              fileTypeName: `${fileTypeConfig.fileTypeName} – ${type}`,
              fileTypeId: fileTypeConfig.fileTypeId,
              type,
            };
          });
        dispatch({
          type: transactionsActionNames.SET_ALL_FILE_TYPES,
          payload: formattedResponse,
        });
      }
    };
